import { ControlSheetRow } from '../types/types'

/**
 * Pairs each inbound trip with its corresponding outbound trip on a control sheet.
 *
 * Control sheet rows can have an inbound trip, an outbound trip, or both.
 * This function sets `oppositeTrip` on each inbound trip so that inbound rows
 * can be sorted/displayed alongside their outbound counterparts.
 *
 * Matching strategy:
 * 1. Direct match — find a row with the same inbound trip number that also has
 *    an outbound trip. The outbound trip number becomes the opposite.
 * 2. Fallback — if no direct match, look at the previous inbound trip (trip
 *    number - 2, since inbound trips are typically even-numbered) and inherit
 *    its opposite. This handles staggered schedules where not every inbound
 *    trip has a directly paired outbound on the same row.
 *
 * Mutates the input rows in place.
 */
export const identifyOppositeTrips = (rows: ControlSheetRow[]) => {
    rows.forEach((row) => {
        if (row.inbound) {
            const inboundTrip = row.inbound.trip

            // Strategy 1: Find a row where this inbound trip number appears alongside an outbound trip
            const outbound = rows.find((r) => r.inbound?.trip === inboundTrip && r.outbound)
            if (outbound) {
                row.inbound.oppositeTrip = outbound.outbound?.trip
            } else {
                // Strategy 2: No direct pair found — look up the previous inbound trip (trip - 2)
                // and reuse its opposite outbound trip number
                const prevInboundTrip = String(Number(inboundTrip) - 2)
                const nextOutbound = rows.find(
                    (r) => r.inbound?.trip === prevInboundTrip && (r.outbound || r.inbound.oppositeTrip)
                )
                if (nextOutbound) {
                    // Use the outbound trip if available, otherwise use the already-resolved opposite
                    row.inbound.oppositeTrip = String(
                        Number(nextOutbound.outbound?.trip ?? nextOutbound.inbound?.oppositeTrip ?? '0')
                    )
                }
            }
        }
    })
}
